import {
  PublicClientApplication,
  type Configuration,
  type AccountInfo,
  type AuthenticationResult,
  InteractionRequiredAuthError,
  BrowserAuthError,
  CacheLookupPolicy,
} from "@azure/msal-browser";
import { useState, useEffect, useCallback, useMemo } from "react";
import { APP_ROLES, type CurrentUserResponse } from "@excalidraw-ms/shared";
import {
  MSAL_CLIENT_ID,
  MSAL_AUTHORITY,
  MSAL_REDIRECT_URI,
  BACKEND_API_SCOPE,
  BACKEND_API_BASE,
} from "../env";
import { clearStagedImportedScene } from "../utils/importScene";
import {
  clearLocalSceneStorage,
  completedSceneStorageLogoutInvalidations,
  completeSceneStorageLogoutInvalidation,
  invalidateSceneStorageWriters,
  retireSceneStorageInvalidation,
  restoreSceneStorageWriters,
  type SceneWriterInvalidation,
} from "../utils/storageRecovery";

/**
 * Scope used for interactive sign-in.
 *
 * Must be a Microsoft Graph scope, never the backend API scope — see
 * `loginRequest` below and docs/designs/ux-rules.md § 6a.
 */
const SIGN_IN_SCOPE = "User.Read";
const PENDING_LOGOUT_INVALIDATION_KEY = "excalidraw-ms:pending-logout-invalidation";
let redirectingLogoutInvalidation: SceneWriterInvalidation | null = null;

// ---------------------------------------------------------------------------
// MSAL Configuration
// ---------------------------------------------------------------------------

const msalConfig: Configuration = {
  auth: {
    clientId: MSAL_CLIENT_ID,
    authority: MSAL_AUTHORITY,
    redirectUri: MSAL_REDIRECT_URI,
    onRedirectNavigate: () => {
      if (redirectingLogoutInvalidation) {
        completeSceneStorageLogoutInvalidation(redirectingLogoutInvalidation);
      }
    },
  },
  cache: {
    cacheLocation: "localStorage",
  },
  system: {
    // Reduce iframe timeout from default 10s → 5s so ssoSilent fails fast
    // when browser sandbox/CSP blocks the hidden iframe (redirect_bridge_timeout).
    iframeBridgeTimeout: 5000,
  },
};

/**
 * Singleton MSAL instance — shared across the app.
 * `initialize()` must be called once before any other method.
 */
let msalInstance: PublicClientApplication | null = null;
let initPromise: Promise<void> | null = null;

export type BackendCapabilityState = "checking" | "full" | "local-only" | "temporarily-unavailable";

export type BackendAccessPhase = "checking-access" | "preparing-cloud" | null;

export type BackendAccessEligibilityState = "checking" | "granted" | "denied";

type CachedBackendCapabilityState = Extract<BackendCapabilityState, "full" | "local-only">;
type CachedBackendAccessEligibilityState = Exclude<BackendAccessEligibilityState, "checking">;

const backendAccessCache = new Map<string, CachedBackendCapabilityState>();
const backendAccessEligibilityCache = new Map<string, CachedBackendAccessEligibilityState>();
const impactDashboardAccessCache = new Map<string, boolean>();

export function clearBackendAccessCache(): void {
  backendAccessCache.clear();
  backendAccessEligibilityCache.clear();
  impactDashboardAccessCache.clear();
}

export function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

/** Ensure MSAL has completed initialization (redirect handling, etc.) */
export async function ensureMsalInitialized(): Promise<void> {
  const pca = getMsalInstance();
  if (!initPromise) {
    initPromise = pca.initialize().then(async () => {
      // Handle redirect promise on page load (for redirect-based login)
      const response = await pca.handleRedirectPromise();
      if (response?.account) {
        completeRedirectSignInSceneStorageLifecycle();
        pca.setActiveAccount(response.account);
      }
    });
  }
  return initPromise;
}

// ---------------------------------------------------------------------------
// Token acquisition helpers
// ---------------------------------------------------------------------------

/**
 * Entra error code returned when the signed-in user is not assigned to the
 * backend app. The backend SP runs with `Assignment required = Yes`, so users
 * outside the access group cannot obtain a backend token at all.
 */
const ASSIGNMENT_DENIED_CODE = "AADSTS50105";

/**
 * Numeric form of {@link ASSIGNMENT_DENIED_CODE}. Entra returns this in the
 * structured `errorCodes` array; the `AADSTS…` prefix appears only in the
 * human-readable description.
 */
const ASSIGNMENT_DENIED_NUMBER = 50105;

/**
 * Thrown when the user is signed in but not assigned to the backend app.
 *
 * This is a terminal, non-retryable state: the user needs access-group
 * membership, not a retry or an interactive prompt. Callers must degrade to
 * Local-Only mode rather than redirecting — an interactive redirect would land
 * the user on an Entra error page they can never get past.
 */
export class BackendAccessDeniedError extends Error {
  constructor() {
    super("Backend access denied: user is not assigned to the Excalidraw-MS backend app");
    this.name = "BackendAccessDeniedError";
  }
}

/**
 * True when an MSAL failure is an Entra "user not assigned to app" denial.
 *
 * Checks the structured `errorCodes` array as well as the free-text fields:
 * MSAL surfaces the numeric code there even when the `AADSTS50105` string is
 * absent from the message, and a missed detection would drop the caller into
 * the redirect path this whole flow exists to avoid.
 */
export function isBackendAccessDenied(err: unknown): boolean {
  if (err instanceof BackendAccessDeniedError) return true;
  if (!err) return false;

  const structuredError = err as {
    errorCode?: unknown;
    errorCodes?: unknown;
    errorMessage?: unknown;
  };
  // Entra's `error_codes` is a JSON array of numbers, but the value reaches us
  // stringified often enough that matching only the number would miss real
  // denials — hence both forms, plus the `AADSTS…` prefixed spelling that shows
  // up in `errorCode`.
  const hasAssignmentDeniedCode = (value: unknown): boolean => {
    if (typeof value === "string") {
      return (
        value.includes(ASSIGNMENT_DENIED_CODE) || value.trim() === String(ASSIGNMENT_DENIED_NUMBER)
      );
    }
    return value === ASSIGNMENT_DENIED_NUMBER;
  };

  if (
    hasAssignmentDeniedCode(structuredError.errorCode) ||
    (Array.isArray(structuredError.errorCodes) &&
      structuredError.errorCodes.some(hasAssignmentDeniedCode))
  ) {
    return true;
  }

  const messages = [
    err instanceof Error ? err.message : String(err),
    typeof structuredError.errorMessage === "string" ? structuredError.errorMessage : "",
  ];
  return messages.some((message) => message.includes(ASSIGNMENT_DENIED_CODE));
}

/**
 * Interactive sign-in request.
 *
 * Uses Graph scopes only — never the backend API scope. Entra evaluates the
 * backend app's `Assignment required` at token issuance for the *requested
 * resource*, so asking for the backend scope here would fail sign-in outright
 * (AADSTS50105) for every unassigned user and make Local-Only mode
 * unreachable. See docs/designs/ux-rules.md § 6a.
 */
const loginRequest = {
  scopes: [SIGN_IN_SCOPE],
  prompt: "select_account" as const,
};

/** Scopes-only request for silent token acquisition (no prompt, no iframe). */
const silentTokenRequest = {
  scopes: [BACKEND_API_SCOPE],
};

/**
 * Acquire an access token for the backend API silently, falling back to redirect.
 *
 * Uses CacheLookupPolicy.AccessTokenAndRefreshToken so MSAL checks the cache
 * and tries the refresh token but **never** spins up a hidden iframe. This
 * avoids the "redirect_bridge_timeout" error seen in hosted/sandboxed
 * environments where iframe navigation is blocked.
 *
 * Throws {@link BackendAccessDeniedError} when the user is signed in but not
 * assigned to the backend app — callers degrade to Local-Only mode.
 */
export async function acquireToken(forceRefresh = false): Promise<string> {
  await ensureMsalInitialized();
  const pca = getMsalInstance();

  const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
  if (!account) {
    // No signed-in user — trigger redirect login
    await pca.loginRedirect(loginRequest);
    // This line is unreachable (redirect navigates away) but keeps TS happy
    throw new Error("Redirecting to login");
  }

  try {
    const result: AuthenticationResult = await pca.acquireTokenSilent({
      ...silentTokenRequest,
      account,
      cacheLookupPolicy: forceRefresh
        ? CacheLookupPolicy.RefreshToken
        : CacheLookupPolicy.AccessTokenAndRefreshToken,
      forceRefresh,
    });
    return result.accessToken;
  } catch (err) {
    // Not assigned to the backend app — terminal. Never redirect: the user
    // would only reach an Entra error page.
    if (isBackendAccessDenied(err)) {
      throw new BackendAccessDeniedError();
    }
    if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
      await pca.acquireTokenRedirect({ ...silentTokenRequest, account });
      throw new Error("Redirecting for token acquisition");
    }
    throw err;
  }
}

/** Key used to remember an explicit sign-out (prevents auto-SSO on next load). */
const SIGNED_OUT_KEY = "excalidraw-ms:signed-out";

/** Sign out (redirect-based). Sets a flag so the app doesn't auto-SSO on return. */
export async function signOut(): Promise<void> {
  await ensureMsalInitialized();
  const pca = getMsalInstance();
  const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
  const invalidation = invalidateSceneStorageWriters();
  try {
    sessionStorage.setItem(PENDING_LOGOUT_INVALIDATION_KEY, invalidation.generation);
  } catch {
    // The redirect must still proceed when session storage is unavailable.
  }
  clearLocalSceneStorage();
  clearStagedImportedScene();
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, "1");
  } catch {
    // The redirect must still proceed when session storage is unavailable.
  }
  try {
    redirectingLogoutInvalidation = invalidation;
    await pca.logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri: window.location.origin,
    });
    completeSceneStorageLogoutInvalidation(invalidation);
  } catch (error) {
    restoreSceneStorageWriters(invalidation);
    clearPendingLogoutInvalidation(invalidation.generation);
    throw error;
  } finally {
    if (redirectingLogoutInvalidation?.generation === invalidation.generation) {
      redirectingLogoutInvalidation = null;
    }
  }
}

function clearPendingLogoutInvalidation(generation: string): void {
  try {
    if (sessionStorage.getItem(PENDING_LOGOUT_INVALIDATION_KEY) === generation) {
      sessionStorage.removeItem(PENDING_LOGOUT_INVALIDATION_KEY);
    }
  } catch {
    // Session storage availability is not required for auth navigation.
  }
}

export function completeRedirectSignInSceneStorageLifecycle(): void {
  let pendingGeneration: string | null = null;
  try {
    pendingGeneration = sessionStorage.getItem(PENDING_LOGOUT_INVALIDATION_KEY);
  } catch {
    // Durable completed markers still allow safe recovery.
  }

  const completedGenerations = completedSceneStorageLogoutInvalidations();
  const generations =
    pendingGeneration && completedGenerations.includes(pendingGeneration)
      ? [pendingGeneration]
      : completedGenerations;
  if (generations.length === 0 || !clearStagedImportedScene()) return;

  for (const generation of generations) {
    if (retireSceneStorageInvalidation(generation)) {
      clearPendingLogoutInvalidation(generation);
    }
  }
}

/** Whether the user explicitly signed out this session. */
export function didExplicitlySignOut(): boolean {
  return sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
}

/** Clear the explicit-sign-out flag (called when user clicks Sign In). */
export function clearSignedOutFlag(): void {
  sessionStorage.removeItem(SIGNED_OUT_KEY);
}

// ---------------------------------------------------------------------------
// Convenience aliases (used by useMsalAuth hook)
// ---------------------------------------------------------------------------

/** Get the currently active account (synchronous — assumes MSAL is initialized). */
export function getActiveAccount(): AccountInfo | null {
  if (!msalInstance) return null;
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/** Alias for acquireToken — named for clarity when acquiring a backend API token. */
export const acquireBackendToken = acquireToken;

/** Sign in via redirect. Clears the explicit-sign-out flag first. */
export async function signIn(): Promise<void> {
  clearSignedOutFlag();
  await ensureMsalInitialized();
  const pca = getMsalInstance();
  await pca.loginRedirect(loginRequest);
}

/**
 * Try silent SSO (no UI). Returns the account if successful, null otherwise.
 * Used on app launch to auto-sign-in users with an existing Entra session.
 */
export async function trySsoSilent(): Promise<AccountInfo | null> {
  await ensureMsalInitialized();
  const pca = getMsalInstance();
  try {
    const result = await pca.ssoSilent({ scopes: [SIGN_IN_SCOPE] });
    if (result?.account) {
      pca.setActiveAccount(result.account);
      return result.account;
    }
  } catch {
    // Silent SSO failed (no session, interaction required, etc.) — expected
  }
  return null;
}

// ---------------------------------------------------------------------------
// React Hooks
// ---------------------------------------------------------------------------

/** Hook that returns the current signed-in account (or null). */
export function useMsalAccount(): AccountInfo | null {
  const [account, setAccount] = useState<AccountInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    ensureMsalInitialized().then(() => {
      if (cancelled) return;
      const pca = getMsalInstance();
      const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
      setAccount(active);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return account;
}

/**
 * Hook that provides a function to get a valid access token.
 * Automatically handles silent refresh and redirect if needed.
 *
 * Usage:
 * ```ts
 * const getToken = useMsalToken();
 * const token = await getToken();
 * ```
 */
export function useMsalToken(): () => Promise<string> {
  return useCallback(() => acquireToken(), []);
}

/**
 * Hook for the full auth state: account, getToken, signIn, signOut, and loading.
 *
 * In dev mode (no MSAL_CLIENT_ID configured), returns a mock account so the UI
 * can be tested without a real Entra ID configuration.
 */
export function useAuth() {
  // Dev mock: when MSAL isn't configured, return a fake account
  const devMode = !MSAL_CLIENT_ID;

  const [account, setAccount] = useState<AccountInfo | null>(devMode ? DEV_MOCK_ACCOUNT : null);
  const [loading, setLoading] = useState(!devMode);
  const [backendCapabilityState, setBackendCapabilityState] = useState<BackendCapabilityState>(
    devMode ? "full" : "checking",
  );
  const [backendAccessPhase, setBackendAccessPhase] = useState<BackendAccessPhase>(
    devMode ? null : "checking-access",
  );
  const [backendAccessCheckedAccountId, setBackendAccessCheckedAccountId] = useState<string | null>(
    devMode ? DEV_MOCK_ACCOUNT.homeAccountId : null,
  );
  const [backendAccessEligibility, setBackendAccessEligibility] = useState<{
    accountId: string | null;
    state: BackendAccessEligibilityState;
  }>({
    accountId: devMode ? DEV_MOCK_ACCOUNT.homeAccountId : null,
    state: devMode ? "granted" : "checking",
  });
  const [backendAccessRetry, setBackendAccessRetry] = useState(0);
  const [canViewImpactDashboard, setCanViewImpactDashboard] = useState(devMode);

  useEffect(() => {
    if (devMode) return;

    let cancelled = false;
    ensureMsalInitialized().then(() => {
      if (cancelled) return;
      const pca = getMsalInstance();
      const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
      setAccount(active);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [devMode]);

  useEffect(() => {
    if (devMode) return;
    if (!account) return;

    let cancelled = false;

    const cachedAccess = backendAccessCache.get(account.homeAccountId);
    if (cachedAccess) {
      setBackendCapabilityState(cachedAccess);
      setBackendAccessEligibility({
        accountId: account.homeAccountId,
        state: cachedAccess === "full" ? "granted" : "denied",
      });
      setBackendAccessPhase(null);
      setBackendAccessCheckedAccountId(account.homeAccountId);
      setCanViewImpactDashboard(impactDashboardAccessCache.get(account.homeAccountId) ?? false);
      return;
    }

    const cachedEligibility = backendAccessEligibilityCache.get(account.homeAccountId);
    setBackendCapabilityState("checking");
    setBackendAccessPhase("checking-access");
    setBackendAccessCheckedAccountId(null);
    setBackendAccessEligibility({
      accountId: account.homeAccountId,
      state: cachedEligibility ?? "checking",
    });

    void (async () => {
      const MAX_RETRIES = 3;
      const RETRY_DELAY_MS = 1500;
      let validatedToken: string | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (cancelled) return;

        try {
          const token = await acquireToken(backendAccessRetry > 0);
          if (cancelled) return;

          const response = await fetch(`${BACKEND_API_BASE}/api/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (cancelled) return;

          if (response.ok) {
            const me = (await response.json()) as CurrentUserResponse;
            const roleGranted = me.hasBackendAccess ?? me.roles?.includes(APP_ROLES.user) ?? false;
            const adminGranted =
              me.canViewImpactDashboard ?? me.roles?.includes(APP_ROLES.admin) ?? false;
            impactDashboardAccessCache.set(account.homeAccountId, adminGranted);
            setCanViewImpactDashboard(adminGranted);
            if (!roleGranted) {
              backendAccessCache.set(account.homeAccountId, "local-only");
              backendAccessEligibilityCache.set(account.homeAccountId, "denied");
              setBackendAccessEligibility({
                accountId: account.homeAccountId,
                state: "denied",
              });
              setBackendCapabilityState("local-only");
              impactDashboardAccessCache.set(account.homeAccountId, false);
              setCanViewImpactDashboard(false);
              setBackendAccessPhase(null);
              setBackendAccessCheckedAccountId(account.homeAccountId);
              return;
            }
            setBackendAccessEligibility({
              accountId: account.homeAccountId,
              state: "granted",
            });
            backendAccessEligibilityCache.set(account.homeAccountId, "granted");
            validatedToken = token;
            break;
          }

          // A role-gate 403 is definitive. A 401 can be transient token/config state.
          if (response.status === 403) {
            backendAccessCache.set(account.homeAccountId, "local-only");
            backendAccessEligibilityCache.set(account.homeAccountId, "denied");
            setBackendAccessEligibility({
              accountId: account.homeAccountId,
              state: "denied",
            });
            setBackendCapabilityState("local-only");
            impactDashboardAccessCache.set(account.homeAccountId, false);
            setCanViewImpactDashboard(false);
            setBackendAccessPhase(null);
            setBackendAccessCheckedAccountId(account.homeAccountId);
            return;
          }

          // 5xx or other transient error — retry
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
            continue;
          }
        } catch (err) {
          if (cancelled) return;

          // Not assigned to the backend app — terminal, do not retry. The user
          // stays fully functional in Local-Only mode and is shown
          // request-access guidance (ux-rules § 6a).
          if (isBackendAccessDenied(err)) {
            backendAccessCache.set(account.homeAccountId, "local-only");
            backendAccessEligibilityCache.set(account.homeAccountId, "denied");
            setBackendAccessEligibility({
              accountId: account.homeAccountId,
              state: "denied",
            });
            setBackendCapabilityState("local-only");
            impactDashboardAccessCache.set(account.homeAccountId, false);
            setCanViewImpactDashboard(false);
            setBackendAccessPhase(null);
            setBackendAccessCheckedAccountId(account.homeAccountId);
            return;
          }

          // Network error — retry
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
            continue;
          }
        }

        setBackendCapabilityState("temporarily-unavailable");
        setBackendAccessPhase(null);
        setBackendAccessCheckedAccountId(account.homeAccountId);
      }

      if (!validatedToken || cancelled) return;
      setBackendAccessPhase("preparing-cloud");

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const preparation = await fetch(`${BACKEND_API_BASE}/api/storage/prepare`, {
            method: "POST",
            headers: { Authorization: "Bearer " + validatedToken },
          });
          if (cancelled) return;
          if (preparation.ok) {
            backendAccessCache.set(account.homeAccountId, "full");
            setBackendCapabilityState("full");
            setBackendAccessPhase(null);
            setBackendAccessCheckedAccountId(account.homeAccountId);
            return;
          }
        } catch {
          if (cancelled) return;
        }

        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1)));
          continue;
        }

        setBackendCapabilityState("temporarily-unavailable");
        setBackendAccessPhase(null);
        setBackendAccessCheckedAccountId(account.homeAccountId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [account, backendAccessRetry, devMode]);

  const doSignIn = useCallback(async () => {
    if (devMode) return;
    clearSignedOutFlag();
    await ensureMsalInitialized();
    const pca = getMsalInstance();
    await pca.loginRedirect(loginRequest);
  }, [devMode]);

  const doSignOut = useCallback(async () => {
    clearBackendAccessCache();
    if (devMode) {
      setAccount(null);
      return;
    }
    await signOut();
  }, [devMode]);

  const retryBackendAccess = useCallback(() => {
    if (!account) return;
    backendAccessCache.delete(account.homeAccountId);
    if (backendAccessEligibilityCache.get(account.homeAccountId) !== "granted") {
      backendAccessEligibilityCache.delete(account.homeAccountId);
      setBackendAccessEligibility({ accountId: account.homeAccountId, state: "checking" });
    }
    impactDashboardAccessCache.delete(account.homeAccountId);
    setCanViewImpactDashboard(false);
    setBackendCapabilityState("checking");
    setBackendAccessPhase("checking-access");
    setBackendAccessCheckedAccountId(null);
    setBackendAccessRetry((current) => current + 1);
  }, [account]);

  const getToken = useMemo(
    () => (devMode ? async () => "dev-mock-token" : () => acquireToken()),
    [devMode],
  );

  const cachedAccess = account ? backendAccessCache.get(account.homeAccountId) : undefined;
  const cachedEligibility = account
    ? backendAccessEligibilityCache.get(account.homeAccountId)
    : undefined;
  const effectiveBackendCapabilityState: BackendCapabilityState = devMode
    ? "full"
    : account
      ? (cachedAccess ??
        (backendAccessCheckedAccountId === account.homeAccountId
          ? backendCapabilityState
          : "checking"))
      : "checking";
  const effectiveHasBackendAccess = effectiveBackendCapabilityState === "full";
  const effectiveBackendAccessEligibilityState: BackendAccessEligibilityState = devMode
    ? "granted"
    : (cachedEligibility ??
      (account && backendAccessEligibility.accountId === account.homeAccountId
        ? backendAccessEligibility.state
        : "checking"));
  const effectiveBackendAccessLoading =
    !devMode &&
    !!account &&
    effectiveBackendCapabilityState === "checking" &&
    backendAccessCheckedAccountId !== account.homeAccountId;
  const effectiveCanViewImpactDashboard = devMode
    ? true
    : account
      ? (impactDashboardAccessCache.get(account.homeAccountId) ?? canViewImpactDashboard)
      : false;

  return {
    account,
    setAccount,
    loading,
    signIn: doSignIn,
    signOut: doSignOut,
    getToken,
    hasBackendAccess: effectiveHasBackendAccess,
    backendAccessLoading: effectiveBackendAccessLoading,
    backendCapabilityState: effectiveBackendCapabilityState,
    backendAccessEligibilityState: effectiveBackendAccessEligibilityState,
    backendAccessPhase,
    retryBackendAccess,
    canViewImpactDashboard: effectiveCanViewImpactDashboard,
    /**
     * True when the user is signed in but not assigned to the backend app.
     * Distinct from a transient backend failure — only this state may surface
     * request-access guidance (ux-rules § 6a).
     */
    backendAccessDenied: !devMode && !!account && effectiveBackendCapabilityState === "local-only",
  };
}

// ---------------------------------------------------------------------------
// Dev mock account (only used when MSAL_CLIENT_ID is not set)
// ---------------------------------------------------------------------------

const DEV_MOCK_ACCOUNT: AccountInfo = {
  homeAccountId: "dev-mock-home-id",
  localAccountId: "dev-mock-local-id",
  environment: "login.microsoftonline.com",
  tenantId: "dev-mock-tenant",
  username: "dev@excalidraw-ms.local",
  name: "Dev User",
  authorityType: "MSSTS",
};
